import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./styles/MessageModal.css";

export type MessageVariant = "info" | "success" | "warning" | "error";

type Action = { label: string; onClick: () => void; primary?: boolean };

type Props = {
    variant?: MessageVariant;
    title?: string;
    children?: React.ReactNode;
    onClose?: () => void;
    actions?: Action[];
    autoDismissMs?: number;
    role?: "status" | "alert";
    inline?: boolean;
};

const Message: React.FC<Props> = ({
    variant = "info",
    title,
    children,
    onClose,
    actions = [],
    autoDismissMs,
    role,
    inline = true,
}) => {
    const ariaRole = role ?? (variant === "error" || variant === "warning" ? "alert" : "status");

    useEffect(() => {
        if (!autoDismissMs || !onClose) return;
        const id = setTimeout(onClose, autoDismissMs);
        return () => clearTimeout(id);
    }, [autoDismissMs, onClose]);

    useEffect(() => {
        const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape" && onClose) onClose(); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    const content = (
        <div className={`psa-message ${inline ? "psa-message--inline" : "psa-message--overlay"}`}>
            {!inline && <div className="psa-message__backdrop" onClick={onClose} />}
            <div className={`psa-message__card psa-message--${variant}`} role={ariaRole} aria-live="assertive" tabIndex={-1}>
                <div className="psa-message__header">
                    <span className="psa-message__title">{title}</span>
                    {onClose && (
                        <button className="psa-message__close" aria-label="Close message" onClick={onClose}>×</button>
                    )}
                </div>
                {children && <div className="psa-message__body">{children}</div>}
                {actions.length > 0 && (
                    <div className="psa-message__actions">
                        {actions.map((a, i) => (
                            <button key={i} className={`psa-btn ${a.primary ? "psa-btn--primary" : ""}`} onClick={a.onClick}>
                                {a.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    return inline ? content : createPortal(content, document.body);
};

export default Message;
